/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import req from '@sportninja/common/api/request'
import colors from '@sportninja/common/constants/appColors'
import { t } from '@sportninja/common/i18n'
import { getAllPlayerTypes } from '@sportninja/common/selectors/types'
import { useReducer, useRef, useState } from 'react'
import { connect } from 'react-redux'

import { FormButton } from '../../Form/css'
import { Flex } from '../../Layout'
import ImportCSVErrorTable from '../ImportCSVErrorTable'
import ImportCSVBanner, { BANNER_TYPES } from '../ImportCSVBanner'
import ImportOfficialsDuplicatePlayer from './ImportOfficialsDuplicatePlayer'
import {
  confirmUploadFile,
  DuplicateRowMismatchError,
  SN_OFFICIAL_ID_COLUMN,
} from '../ImportTeamRoster/utils/applyDuplicateDecisions'
import {
  duplicateDecisionsReducer,
  ignoredLines,
  INITIAL_DUPLICATE_DECISIONS,
  isComplete,
} from '../ImportTeamRoster/utils/duplicateDecisions'
import { importCsvLayout } from '../ImportTeamRoster/utils/importCsvRows'
import { importRequestOutcome } from '../ImportTeamRoster/utils/importRequestOutcome'
import { font } from '../../css'

/**
 * The officials import's duplicate review. Each choice is applied to the row
 * the server flagged, in the file's own SN Official ID column, through the
 * roster import's rewrite; a row it cannot line up stops the import.
 */
const ImportOfficialsErrorDuplicates = ({
  conflicts = [],
  playerTypes,
  teamName,
  resetFile,
  csvRows,
  file,
  submitUrl,
  onUploadComplete,
  onImportError,
  onOutcomeUnknown,
  setLoading,
  problemRowCount = 0,
}) => {
  // Set before the request goes out, so a second submit event sends nothing.
  // Left set once the outcome is unknown: this review never posts again.
  const inFlight = useRef(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(false)
  const [outcomeUnknown, setOutcomeUnknown] = useState(false)
  const [shouldHighlightDiff, setShouldHighlightDiff] = useState(false)
  const [{ decisions }, dispatch] = useReducer(
    duplicateDecisionsReducer,
    INITIAL_DUPLICATE_DECISIONS
  )

  const allDecided = isComplete(decisions, conflicts)
  const dataRowCount = importCsvLayout(csvRows).dataRows.length

  const onSubmit = async (e) => {
    e.preventDefault()

    if (inFlight.current || !allDecided) {
      return
    }

    let upload
    try {
      upload = confirmUploadFile(
        file,
        csvRows,
        conflicts,
        decisions,
        SN_OFFICIAL_ID_COLUMN
      )
    } catch (rewriteError) {
      setError(
        rewriteError instanceof DuplicateRowMismatchError
          ? t('common:importDuplicatesRowMismatch', { line: rewriteError.line })
          : rewriteError.message
      )
      return
    }

    inFlight.current = true
    setError(false)
    setSubmitting(true)
    setLoading(true)

    const body = new FormData()
    body.append('file', upload)
    body.append('conflicts', 'ignore')

    try {
      const response = await req(submitUrl, {
        body,
        method: 'POST',
        skipAutoContentType: true,
        // One attempt: a replay after a lost response would create every new official again.
        maxAttempts: 1,
        reportGlobalError: false,
      })
      onUploadComplete(response)
    } catch (requestError) {
      const outcome = importRequestOutcome(requestError)

      if (outcome === 'unknown') {
        setOutcomeUnknown(true)
        onOutcomeUnknown()
      } else {
        inFlight.current = false
        // A 409 is the import's own report — rows failed, others may have been
        // imported — and belongs to the dialog's report, not a bare error line
        // (SN-4838). The rows ignored here left the file, so the report is
        // renumbered as the admin's file.
        if (outcome === 'report') {
          onImportError(requestError, ignoredLines(decisions))
        } else {
          setError(
            requestError?.message || 'There was a problem importing your file.'
          )
        }
      }
    } finally {
      setSubmitting(false)
      setLoading(false)
    }
  }

  return (
    <>
      <ImportCSVBanner
        type={BANNER_TYPES.info.type}
        // The report's problem rows will not import whatever is decided here (SN-4838).
        message={`${
          dataRowCount - problemRowCount
        } officials to import. Please review ${
          conflicts.length
        } potential duplicates below:`}
      />
      {outcomeUnknown ? (
        <ImportCSVBanner
          type={BANNER_TYPES.error.type}
          message={t('common:importOfficialsOutcomeUnknown')}
        />
      ) : null}
      <form
        id='team-roster-duplicates'
        action='/'
        method='GET'
        onSubmit={onSubmit}
        css={css`
          overflow: auto;
          margin-bottom: 16px;
          border-bottom-left-radius: 4px;
          border-bottom-right-radius: 4px;
        `}
      >
        {error && (
          <ImportCSVBanner type={BANNER_TYPES.error.type} message={error} />
        )}
        <fieldset
          disabled={submitting}
          css={css`
            min-width: 0;
            margin: 0;
            padding: 0;
            border: 0;
          `}
        >
          <ImportCSVErrorTable
            body={
              Array.isArray(conflicts) &&
              conflicts.map((player, i) => {
                const { line } = player

                return (
                  <tr
                    key={line}
                    className='row'
                    css={
                      i !== conflicts.length - 1 &&
                      css`
                        border-bottom: 1px solid ${colors.PALE_SKY};
                      `
                    }
                  >
                    <td>
                      <ImportOfficialsDuplicatePlayer
                        line={line}
                        player={player}
                        decision={decisions[line]}
                        dispatch={dispatch}
                        playerTypes={playerTypes}
                        teamName={teamName}
                        shouldHighlightDiff={shouldHighlightDiff}
                      />
                    </td>
                  </tr>
                )
              })
            }
          />
        </fieldset>
      </form>
      <Flex alignItems='center'>
        <button
          type='button'
          className={shouldHighlightDiff ? 'active' : ''}
          onClick={() => {
            setShouldHighlightDiff(!shouldHighlightDiff)
          }}
          css={css`
            display: flex;
            align-items: center;
            margin-bottom: 18px;

            span {
              margin-left: 10px;
              ${font.body}
              font-size: 16px;
              color: ${shouldHighlightDiff ? 'white' : '#9fa3a9'};
            }
          `}
        >
          <div
            css={css`
              display: flex;
              align-items: center;
              width: 20px;
              height: 10px;
              border-radius: 5px;
              padding: 0 2px;
              background-color: ${shouldHighlightDiff
                ? colors.DEFAULT_FLAIR
                : '#9fa3a9'};

              .baton {
                background-color: #191b1f;
                width: 7px;
                height: 7px;
                border-radius: 50%;
                transition: transform 0.1s ease-in-out;
              }

              .active & {
                .baton {
                  transform: translateX(9px);
                }
              }
            `}
          >
            <div className='baton' />
          </div>
          <span>Highlight differences</span>
        </button>
      </Flex>
      <Flex alignItems='center' justifyContent='space-between'>
        <FormButton
          disabled={submitting}
          css={css`
            width: 160px;
            height: 40px;
            border: 2px solid ${colors.DEFAULT_FLAIR};
            background-image: none;
            color: white;

            &:hover {
              background-image: none !important;
            }
          `}
          onClick={resetFile}
        >
          Cancel {t('common:import')}
        </FormButton>
        <FormButton
          form='team-roster-duplicates'
          busy={submitting}
          disabled={submitting || outcomeUnknown || !allDecided}
          isSubmit
          css={css`
            width: 160px;
            height: 40px;
            background-color: ${colors.DEFAULT_FLAIR};
            background-image: none;

            &:hover {
              background-image: none !important;
            }
          `}
        >
          Confirm & {t('common:import')}
        </FormButton>
      </Flex>
    </>
  )
}

const mapStateToProps = (state) => {
  return {
    playerTypes: getAllPlayerTypes(state),
  }
}

export default connect(mapStateToProps)(ImportOfficialsErrorDuplicates)
